import { buttonClassName, ButtonVariant } from '@/components/form/buttonHelper';
import { Step } from '@/components/multistep/Questionnaire';
import SiDownload from '@/icons/SiDownload';
import { Product } from '@/services/api/contracts/catalog/types';
import { useProducts } from '@/services/api/hooks/product';
import { toMajor } from '@/utils/price';
import { getNodeString } from '@udecode/plate-common';
import { useMemo } from 'react';

const BASE_HEADINGS = [
  'Name',
  'ManufacturerSKU',
  'Manufacturer',
  'Category',
  'Description',
  'Price',
  'Region',
  'BasicBetterBest',
  'Margin',
  'Replacements'
];

const escapeCsvValue = (value: string) => {
  let str = String(value);

  if (str.startsWith('=') || str.startsWith('+') || str.startsWith('-') || str.startsWith('@')) {
    str = `'${str}`;
  }

  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    str = `"${str.replace(/"/g, '""')}"`;
  }

  return str;
};

const buildCsv = (items: Product[]): string => {
  const specKeys = new Set<string>();
  const otherSpecColumns: string[] = [];

  items.forEach(item => {
    let otherSpecIndex = 1; // Track multiple "Other" specs dynamically

    item.propertyBag?.properties?.forEach(spec => {
      if (spec.iconId) {
        specKeys.add(`Spec:${spec.iconId}`);
      } else {
        const otherSpecKey = `Spec:Other${otherSpecIndex}`;
        if (!otherSpecColumns.includes(otherSpecKey)) {
          otherSpecColumns.push(otherSpecKey);
        }
        otherSpecIndex++;
      }
    });
  });

  const dynamicHeadings = [...specKeys, ...otherSpecColumns];
  const finalHeadings = [...BASE_HEADINGS, ...dynamicHeadings];

  const rows = items.map(item => {
    const description = item.description?.value?.map(n => getNodeString(n)).join(' ');

    const baseRow = [
      item.name || '',
      item.productId || '',
      item.manufacturer.name || '',
      item.category.name || '',
      description ? escapeCsvValue(description) : '',
      toMajor(item.price?.price || 0),
      item.region.code || '',
      item.propertyBag?.basicBetterBest !== '-' ? item.propertyBag?.basicBetterBest : '',
      item.price?.margins[0].margin || 0,
      ''
    ];

    const specValues: Record<string, string> = {};
    const otherSpecs: string[] = [];

    item.propertyBag?.properties?.forEach(spec => {
      if (spec.iconId) {
        specValues[`Spec:${spec.iconId}`] = spec.text;
      } else {
        otherSpecs.push(spec.text);
      }
    });

    const otherSpecValues: Record<string, string> = {};
    otherSpecs.forEach((text, index) => {
      otherSpecValues[`Spec:Other${index + 1}`] = text;
    });

    const dynamicValues = dynamicHeadings.map(heading =>
      otherSpecValues[heading]
        ? escapeCsvValue(otherSpecValues[heading] || '')
        : escapeCsvValue(specValues[heading] || '')
    );

    const test = [...baseRow, ...dynamicValues];
    return test;
  });

  const head = finalHeadings.join(',');
  const result = rows.reduce((template, row) => {
    return template + '\n' + row.join(',');
  }, head);

  return result;
};

export const DownloadCatalogue = () => {
  const [products] = useProducts();

  const template = useMemo(() => {
    const standardProducts = products.filter(p => !p.isRateCardItem);
    const csv = buildCsv(standardProducts);
    return URL.createObjectURL(new Blob([csv], { type: 'octet/stream' }));
  }, [products]);

  return (
    <Step controls="manual" id="download" title="Download" description="Download a spreadsheet of the DaaS Catalogue">
      <div className="flex w-full flex-col gap-2">
        <a className={buttonClassName(ButtonVariant.Mono)} href={template} download="daas-catalogue.csv">
          <SiDownload />
          <span>Download</span>
        </a>
      </div>
    </Step>
  );
};
